import { GONDOLA_POR_ID } from './catalogo';

/**
 * La aritmética de la góndola de canal moderno: cuánto espacio ocupa cada marca.
 *
 * ## Cómo está guardada la góndola
 *
 * `ficha.gondolas.<categoría>.espacio` trae `{ cols, filas, marcas[], grid }`. `grid` es una
 * cadena de **un carácter por celda**:
 *
 * - `cols = módulos × 2`, `filas = niveles`. O sea que **una celda es medio módulo de ancho por
 *   un nivel de alto**. No es una «cara» ni un facing: es una porción de estante.
 * - El recorrido es **por filas**, de arriba abajo: `índice = fila × cols + columna`, y la fila 0
 *   es la repisa de más arriba.
 * - El dígito `d` apunta a `marcas[d - 1]` (uno, no cero). `'0'` es celda vacía.
 *
 * Ejemplo real, `cols:16 filas:5`, cinco veces `1111111111113333`:
 * 60 celdas de Gillette y 20 de Schick sobre 80 pintadas → 75 % y 25 %.
 */

export interface EspacioGondola {
  cols: number;
  filas: number;
  marcas: readonly string[];
  grid: string;
}

export interface ParticipacionMarca {
  marca: string;
  /** El dígito con el que se pinta. Sirve para el color. */
  digito: number;
  celdas: number;
  /** Sobre las celdas PINTADAS, no sobre el mueble entero. */
  pct: number;
}

export interface Participacion {
  /** `false` cuando no se puede calcular. En ese caso no hay porcentaje: no hay un cero. */
  medida: boolean;
  motivo?: 'sin_grilla' | 'largo_no_cuadra' | 'nada_pintado';
  marcas: ParticipacionMarca[];
  pintadas: number;
  celdas: number;
  /** Cuánto del mueble se llegó a pintar. Este SÍ va sobre `cols × filas`. */
  pctPintado: number;
}

/**
 * 🔴 **El denominador de la participación son las celdas PINTADAS, no el mueble entero.**
 *
 * Es la misma fórmula que usa el formulario en el celular, y es la correcta: si la auditora pintó
 * media góndola, lo que midió es el reparto de esa mitad. Dividir por `cols × filas` mezclaría dos
 * cosas —cuánto le toca a cada marca y cuánto alcanzó a medirse— en un solo número que no
 * significa ninguna de las dos.
 *
 * Cuánto se alcanzó a medir es una pregunta legítima y va aparte, en `pctPintado`.
 *
 * ⚠️ **Con `Math.round` la suma puede dar 99 o 101, y NO se fuerza a 100.** Forzarla obliga a
 * elegir a qué marca se le regala o se le quita un punto, y ese punto es inventado. Un 101 visible
 * es honesto; un 100 acomodado no.
 */
export function participacionDeGondola(esp: EspacioGondola | null | undefined): Participacion {
  const vacia: Participacion = {
    medida: false, marcas: [], pintadas: 0, celdas: 0, pctPintado: 0,
  };
  if (esp == null) return { ...vacia, motivo: 'sin_grilla' };

  const celdas = esp.cols * esp.filas;
  const grid = esp.grid ?? '';
  if (grid === '') return { ...vacia, celdas, motivo: 'sin_grilla' };

  // Cambiar módulos o niveles después de pintar BORRA la grilla entera sin avisar, así que una
  // longitud que no cuadra es un estado real y no un dato corrupto. Se dice, no se calcula.
  if (grid.length !== celdas) return { ...vacia, celdas, motivo: 'largo_no_cuadra' };

  // Con más de 9 marcas el dígito ocuparía dos caracteres y todo el esquema se cae. Hoy las dos
  // góndolas tienen 5. Mejor romper ruidosamente que devolver un número plausible y falso.
  if (esp.marcas.length > 9) {
    throw new Error(
      `La grilla no admite más de 9 marcas y esta trae ${esp.marcas.length}: el dígito dejaría de `
      + 'ser un solo carácter.',
    );
  }

  const cel = grid.split('');
  const pintadas = cel.filter((c) => c !== '0').length;
  const pctPintado = celdas === 0 ? 0 : Math.round((pintadas / celdas) * 100);
  if (pintadas === 0) {
    return { medida: false, motivo: 'nada_pintado', marcas: [], pintadas: 0, celdas, pctPintado: 0 };
  }

  const marcas = esp.marcas.map((marca, i) => {
    const digito = i + 1;
    const n = cel.filter((c) => c === String(digito)).length;
    return { marca, digito, celdas: n, pct: Math.round((n / pintadas) * 100) };
  });

  return { medida: true, marcas, pintadas, celdas, pctPintado };
}

/**
 * Cuánto de lo pintado es de P&G.
 *
 * 🔴 **En afeitado son DOS dígitos: Gillette (1) y Venus (2).**
 *
 * El formulario del celular calcula sus dos derivados contando solo el dígito `'1'`. Copiar esa
 * fórmula tal cual **subestima a P&G en afeitado**, porque deja a Venus del lado de nadie. Acá la
 * lista de marcas de P&G sale del catálogo, no de una posición cableada.
 */
export function participacionPg(
  gondolaId: string, esp: EspacioGondola | null | undefined,
): { medida: boolean; pct: number; celdas: number; pintadas: number } {
  const p = participacionDeGondola(esp);
  if (!p.medida) return { medida: false, pct: 0, celdas: 0, pintadas: p.pintadas };
  const pg = GONDOLA_POR_ID[gondolaId]?.pg ?? [];
  const celdas = p.marcas.filter((m) => pg.includes(m.marca)).reduce((s, m) => s + m.celdas, 0);
  return {
    medida: true,
    celdas,
    pintadas: p.pintadas,
    pct: Math.round((celdas / p.pintadas) * 100),
  };
}

/**
 * Qué filas están a la altura de los ojos, según cuántas repisas tenga el mueble.
 *
 * 🔴 Devuelve **`null` para las de 3 niveles**: no tienen nivel de ojos («arriba / a la mano /
 * abajo»), y hay 6 fichas así en producción. Asignarles una fila cualquiera y promediarla con las
 * demás produce un indicador que dice «nivel de los ojos» sobre una repisa que no lo es.
 *
 * Y devuelve **dos filas para las de 6**, porque el formulario rotula dos repisas «a los ojos».
 * Tomar una sola mediría la mitad del nivel.
 */
export function filasOjos(filas: number): number[] | null {
  if (filas === 3) return null;
  if (filas === 4 || filas === 5) return [1];
  if (filas === 6) return [1, 2];
  return null;
}

/**
 * Qué porcentaje del nivel de los ojos es de P&G.
 *
 * El denominador son las celdas **pintadas de esas filas**, no el ancho del mueble: una fila a
 * medio pintar mide el reparto de lo que hay, igual que la góndola entera.
 */
export function ojosPg(
  gondolaId: string, esp: EspacioGondola | null | undefined,
): { aplica: boolean; medida: boolean; pct: number } {
  if (esp == null || esp.grid === '' || esp.grid.length !== esp.cols * esp.filas) {
    return { aplica: true, medida: false, pct: 0 };
  }
  const filas = filasOjos(esp.filas);
  if (filas === null) return { aplica: false, medida: false, pct: 0 };

  const pg = GONDOLA_POR_ID[gondolaId]?.pg ?? [];
  const digitosPg = esp.marcas
    .map((m, i) => (pg.includes(m) ? String(i + 1) : null))
    .filter((d): d is string => d !== null);

  let pintadas = 0;
  let deLosNuestros = 0;
  for (const f of filas) {
    for (let c = 0; c < esp.cols; c += 1) {
      const ch = esp.grid[f * esp.cols + c];
      if (ch === undefined || ch === '0') continue;
      pintadas += 1;
      if (digitosPg.includes(ch)) deLosNuestros += 1;
    }
  }
  if (pintadas === 0) return { aplica: true, medida: false, pct: 0 };
  return { aplica: true, medida: true, pct: Math.round((deLosNuestros / pintadas) * 100) };
}

/**
 * En cuántos pedazos separados está el bloque de una marca.
 *
 * Un bloque partido es una historia comercial: el mismo espacio total repartido en tres islas se
 * ve mucho menos que en un bloque, y es lo que se negocia con la cadena. Vecindad de 4 —arriba,
 * abajo, izquierda, derecha—: dos celdas que solo se tocan en diagonal no forman un bloque a la
 * vista de nadie.
 */
export function pedazos(esp: EspacioGondola | null | undefined, digitos: readonly string[]): number {
  if (esp == null || esp.grid === '' || esp.grid.length !== esp.cols * esp.filas) return 0;
  const { cols, filas, grid } = esp;
  const visto = new Array<boolean>(cols * filas).fill(false);
  const mio = (i: number) => digitos.includes(grid[i]);
  let n = 0;
  for (let i = 0; i < cols * filas; i += 1) {
    if (visto[i] || !mio(i)) continue;
    n += 1;
    const pila = [i];
    visto[i] = true;
    while (pila.length > 0) {
      const j = pila.pop() as number;
      const f = Math.floor(j / cols);
      const c = j % cols;
      const vecinos = [
        f > 0 ? j - cols : -1,
        f < filas - 1 ? j + cols : -1,
        c > 0 ? j - 1 : -1,
        c < cols - 1 ? j + 1 : -1,
      ];
      for (const v of vecinos) {
        if (v >= 0 && !visto[v] && mio(v)) { visto[v] = true; pila.push(v); }
      }
    }
  }
  return n;
}

/** Los dígitos de P&G de una góndola. Para pasarle a `pedazos`. */
export function digitosPgDe(gondolaId: string, esp: EspacioGondola | null | undefined): string[] {
  if (esp == null) return [];
  const pg = GONDOLA_POR_ID[gondolaId]?.pg ?? [];
  return esp.marcas.map((m, i) => (pg.includes(m) ? String(i + 1) : null))
    .filter((d): d is string => d !== null);
}

/** La grilla desdoblada en filas, para dibujarla. Cada valor es el dígito de esa celda. */
export function desdoblarGrid(esp: EspacioGondola): string[][] {
  const out: string[][] = [];
  for (let f = 0; f < esp.filas; f += 1) {
    out.push(esp.grid.slice(f * esp.cols, (f + 1) * esp.cols).split(''));
  }
  return out;
}

/**
 * Los precios del canal moderno son **texto libre sin validar**.
 *
 * El widget del formulario es un `<input type="text" inputMode="decimal">` sin parseo: guarda lo
 * que se teclee. El `S/` es decorativo y no está en el valor. Por eso esto devuelve `null` en vez
 * de `NaN` cuando no entiende, y la pantalla muestra el texto crudo: un `NaN` en la columna de
 * precio se lee como una falla del sistema cuando lo que pasó es que alguien escribió «s/d».
 */
export function precioModerno(v: string | null | undefined): number | null {
  if (v == null) return null;
  const limpio = v.replace(/\s/g, '').replace(/^S\/\.?/i, '').replace(',', '.');
  if (limpio === '') return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
}
